import React, { useState } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  Card,
  Typography,
  Popconfirm,
  message,
  Row,
  Col,
  Dropdown,
  Modal,
  Form,
} from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';
import {
  PlusOutlined,
  DeleteOutlined,
  SearchOutlined,
  EyeOutlined,
  CopyOutlined,
  MoreOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { campaignsService } from '../../api/campaigns';
import { usersService } from '../../api/users';
import { Campaign, CampaignStatus, CreateCampaignRequest } from '../../types';
import { useAuth } from '../../hooks/useAuth';

const { Title } = Typography;
const { Search } = Input;

const CampaignsList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasRole } = useAuth();
  const { t } = useTranslation();
  
  const [searchText, setSearchText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<CampaignStatus | undefined>();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [form] = Form.useForm();

  const { data: campaignsData, isLoading } = useQuery({
    queryKey: ['campaigns', pagination.current, pagination.pageSize, searchText, selectedStatus],
    queryFn: () => campaignsService.getCampaigns(
      pagination.current,
      pagination.pageSize,
      searchText,
      selectedStatus
    ),
  });

  const { data: usersData } = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => usersService.getUsers(undefined, 100), // Get all users for dropdown
  });

  const deleteMutation = useMutation({
    mutationFn: campaignsService.deleteCampaign,
    onSuccess: () => {
      message.success(t('messages.campaignDeleted'));
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (error: Error) => {
      message.error(error?.message || t('messages.errorDeletingCampaign'));
    },
  });

  const createMutation = useMutation({
    mutationFn: campaignsService.createCampaign,
    onSuccess: () => {
      message.success(t('messages.campaignCreated'));
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      setIsCreateModalVisible(false);
      form.resetFields();
    },
    onError: (error: Error) => {
      message.error(error?.message || t('messages.errorCreatingCampaign'));
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: CampaignStatus }) =>
      campaignsService.updateCampaignStatus(id, status),
    onSuccess: () => {
      message.success('Статус кампании изменен');
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (error: Error) => {
      message.error(error?.message || 'Ошибка при изменении статуса');
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      campaignsService.duplicateCampaign(id, name),
    onSuccess: () => {
      message.success('Кампания успешно дублирована');
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (error: Error) => {
      message.error(error?.message || 'Ошибка при дублировании кампании');
    },
  });

  const getStatusColor = (status: CampaignStatus) => {
    const colors = {
      ACTIVE: 'green',
      INACTIVE: 'red',
      DRAFT: 'orange',
      ARCHIVED: 'gray',
    };
    return colors[status] || 'default';
  };

  const getStatusDisplayName = (status: CampaignStatus) => {
    return t(`campaignStatuses.${status}`, status);
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleStatusChange = (id: string, newStatus: CampaignStatus) => {
    updateStatusMutation.mutate({ id, status: newStatus });
  };

  const handleDuplicate = (campaign: Campaign) => {
    Modal.confirm({
      title: 'Дублировать кампанию',
      content: (
        <div>
          <p>Введите название для новой кампании:</p>
          <Input 
            placeholder={`Копия ${campaign.name}`}
            id="duplicate-name-input"
          />
        </div>
      ),
      onOk: () => {
        const input = document.getElementById('duplicate-name-input') as HTMLInputElement;
        const newName = input?.value || `Копия ${campaign.name}`;
        duplicateMutation.mutate({ id: campaign.id, name: newName });
      },
    });
  };

  const handleTableChange: TableProps<Campaign>['onChange'] = (pagination) => {
    setPagination({
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
      total: pagination.total || 0,
    });
  };

  const handleCreateCampaign = (values: CreateCampaignRequest) => {
    createMutation.mutate(values);
  };

  const columns: ColumnsType<Campaign> = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (name: string, record) => (
        <Button
          type="link"
          onClick={() => navigate(`/campaigns/${record.id}`)}
          style={{ padding: 0, height: 'auto' }}
        >
          {name}
        </Button>
      ),
    },
    {
      title: 'Поддомен',
      dataIndex: 'subdomain',
      key: 'subdomain',
      render: (subdomain: string) => (
        <code>{subdomain}.oxapp.io</code>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: CampaignStatus) => (
        <Tag color={getStatusColor(status)}>
          {getStatusDisplayName(status)}
        </Tag>
      ),
      filters: [
        { text: 'Активная', value: 'ACTIVE' },
        { text: 'Неактивная', value: 'INACTIVE' },
        { text: 'Черновик', value: 'DRAFT' },
        { text: 'Архивная', value: 'ARCHIVED' },
      ],
    },
    {
      title: 'Владелец',
      key: 'owner',
      render: (_, record) => record.owner ? 
        `${record.owner.firstName} ${record.owner.lastName}` : 
        'Не назначен',
    },
    {
      title: 'Создана',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('ru-RU'),
      sorter: true,
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 200,
      render: (_, record) => {
        const menuItems = [
          {
            key: 'view',
            icon: <EyeOutlined />,
            label: 'Просмотр',
            onClick: () => navigate(`/campaigns/${record.id}`),
          },
          {
            key: 'duplicate',
            icon: <CopyOutlined />,
            label: 'Дублировать',
            onClick: () => handleDuplicate(record),
          },
          ...(record.status === 'ACTIVE' ? [{
            key: 'pause',
            icon: <PauseCircleOutlined />,
            label: 'Приостановить',
            onClick: () => handleStatusChange(record.id, 'INACTIVE'),
          }] : [{
            key: 'activate',
            icon: <PlayCircleOutlined />,
            label: 'Активировать',
            onClick: () => handleStatusChange(record.id, 'ACTIVE'),
          }]),
          ...(hasRole(['SUPERADMIN', 'ADMIN']) ? [{
            type: 'divider' as const,
          }, {
            key: 'delete',
            icon: <DeleteOutlined />,
            label: 'Удалить',
            danger: true,
            onClick: () => handleDelete(record.id),
          }] : []),
        ];

        return (
          <Space size="middle">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/campaigns/${record.id}`)}
            />
            <Button
              type="link"
              icon={record.status === 'ACTIVE' ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
              onClick={() => handleStatusChange(
                record.id, 
                record.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
              )}
            />
            {hasRole(['SUPERADMIN', 'ADMIN']) && (
              <Popconfirm
                title="Удалить кампанию?"
                description="Это действие нельзя будет отменить."
                onConfirm={() => handleDelete(record.id)}
                okText="Да"
                cancelText="Нет"
              >
                <Button
                  type="link"
                  icon={<DeleteOutlined />}
                  danger
                />
              </Popconfirm>
            )}
            <Dropdown menu={{ items: menuItems }}>
              <Button type="link" icon={<MoreOutlined />} />
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2}>{t('campaigns.title')}</Title>
        </Col>
        <Col>
          {hasRole(['SUPERADMIN', 'ADMIN']) && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsCreateModalVisible(true)}
            >
              {t('campaigns.createCampaign')}
            </Button>
          )}
        </Col>
      </Row>

      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={12}>
            <Search
              placeholder="Поиск по названию или поддомену"
              allowClear
              onSearch={setSearchText}
              style={{ width: '100%' }}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col xs={24} md={8}>
            <Select
              placeholder="Фильтр по статусу"
              allowClear
              style={{ width: '100%' }}
              onChange={setSelectedStatus}
              options={[
                { label: 'Активная', value: 'ACTIVE' },
                { label: 'Неактивная', value: 'INACTIVE' },
                { label: 'Черновик', value: 'DRAFT' },
                { label: 'Архивная', value: 'ARCHIVED' },
              ]}
            />
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={campaignsData?.items || []}
          loading={isLoading}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: campaignsData?.items?.length || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} из ${total} записей`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
        />
      </Card>

      {/* Create Campaign Modal */}
      <Modal
        title="Создать новую кампанию"
        open={isCreateModalVisible}
        onCancel={() => {
          setIsCreateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateCampaign}
        >
          <Form.Item
            name="name"
            label="Название кампании"
            rules={[
              { required: true, message: 'Пожалуйста, введите название' },
              { min: 3, message: 'Название должно содержать минимум 3 символа' },
            ]}
          >
            <Input placeholder="Введите название кампании" />
          </Form.Item>

          <Form.Item
            name="subdomain"
            label="Поддомен"
            rules={[
              { required: true, message: 'Пожалуйста, введите поддомен' },
              { 
                pattern: /^[a-z0-9-]+$/, 
                message: 'Поддомен может содержать только латинские буквы, цифры и дефисы' 
              },
            ]}
          >
            <Input 
              placeholder="my-campaign" 
              addonAfter=".oxapp.io"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="ownerId"
            label="Владелец"
            rules={[{ required: true, message: 'Пожалуйста, выберите владельца' }]}
          >
            <Select
              placeholder="Выберите владельца кампании"
              showSearch
              filterOption={(input, option) =>
                option?.children ? 
                  String(option.children).toLowerCase().includes(input.toLowerCase()) : 
                  false
              }
            >
              {usersData?.items.map((user: { id: string; firstName: string; lastName: string; email: string }) => (
                <Select.Option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName} ({user.email})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => {
                setIsCreateModalVisible(false);
                form.resetFields();
              }}>
                Отмена
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={createMutation.isPending}
              >
                Создать
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CampaignsList;
